import { useEffect, useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa6';
import { HiOutlineEnvelope } from 'react-icons/hi2';
import { LuPackageSearch } from 'react-icons/lu';
import { SiTiktok } from 'react-icons/si';
import type { AppConfig } from '../../shared/config';
import { build17TrackUrl, logoAssets } from '../../shared/config';
import type { OrderLookupResult, OrderSummary, ShipmentPackage } from '../../shared/orders';
import './customer.css';

function visitorId() {
  const key = 'tuftlet-visitor-id';
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(key, created);
  return created;
}

function maskedOrderNumber(value: string) {
  const characters = Array.from(value);
  const visible = Math.min(6, Math.max(2, characters.length - 2));
  const hidden = Math.min(6, Math.max(2, characters.length - visible));
  return `${'•'.repeat(hidden)}${characters.slice(-visible).join('')}`;
}

function whatsappUrl(number: string, message: string): string | null {
  const digits = number.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}` : null;
}

function ActionIcon({ type }: { type: 'whatsapp' | 'email' | 'tracking' }) {
  const iconProps = { 'aria-hidden': true, className: 'action-icon', 'data-testid': 'action-icon' } as const;
  if (type === 'whatsapp') return <FaWhatsapp {...iconProps} />;
  if (type === 'email') return <HiOutlineEnvelope {...iconProps} />;
  return <LuPackageSearch {...iconProps} />;
}

function PackageGroup({ title, description, type, items, onCopy }: {
  title: string;
  description?: string;
  type: 'gift' | 'main';
  items: ShipmentPackage[];
  onCopy: (trackingNumber: string) => void;
}) {
  if (!items.length) return null;
  return <section className="package-group">
    <h4>{title}</h4>
    {description && <p className="package-description">{description}</p>}
    {items.map((item) => {
      const trackingUrl = build17TrackUrl(item.tracking_number);
      return <div className="shipment" key={item.tracking_number}>
        <span className="tracking-label">Tracking number</span>
        <strong className="tracking-number">{item.tracking_number}</strong>
        <div className="shipment-actions">
          <button type="button" className="compact-button" aria-label={`Copy ${type} tracking number`} onClick={() => onCopy(item.tracking_number)}>Copy</button>
          <a className="compact-button primary-compact" href={trackingUrl} target="_blank" rel="noopener noreferrer">Track on 17TRACK</a>
        </div>
      </div>;
    })}
  </section>;
}

function OrderCard({ order, index, whatsappNumber, onCopy, revealOrderNumber = false }: {
  order: OrderSummary;
  index: number;
  whatsappNumber: string;
  onCopy: (trackingNumber: string) => void;
  revealOrderNumber?: boolean;
}) {
  const heading = index === 0 ? 'Most recent order' : 'Previous order';
  const orderNumber = revealOrderNumber ? order.order_number : maskedOrderNumber(order.order_number);
  const helpUrl = whatsappUrl(whatsappNumber, `Hi Tuftlet, I need help with TikTok order ${order.order_number}.`);
  const date = order.order_created_at
    ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(order.order_created_at))
    : 'Not available';
  return <article className="order-card" aria-label={heading}>
    <div className="order-heading"><h3>{heading}</h3><span>{date}</span></div>
    <div className="order-number"><span>TikTok order</span><strong>{orderNumber}</strong></div>
    {order.main_packages.length > 0 && <>
      <PackageGroup title="Your Tuftlet package" description="This is the package containing your handmade Tuftlet order." type="main" items={order.main_packages} onCopy={onCopy} />
    </>}
    {!order.main_packages.length && <p className="empty-packages">We found your order. Tracking details are not available yet. Please check again later or contact us on WhatsApp.</p>}
    {helpUrl && <a className="order-help" href={helpUrl} target="_blank" rel="noreferrer">Chat on WhatsApp</a>}
  </article>;
}

export function CustomerPage({ config, fetcher = fetch }: { config: AppConfig; fetcher?: typeof fetch }) {
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [shippingName, setShippingName] = useState('');
  const [result, setResult] = useState<OrderLookupResult | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const trackingRef = useRef<HTMLFormElement>(null);
  const orderInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLElement>(null);

  useEffect(() => {
    fetcher('/api/events', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ type: 'visit', visitor_id: visitorId() }), keepalive: true }).catch(() => undefined);
  }, [fetcher]);

  useEffect(() => {
    if (trackingOpen) trackingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [trackingOpen]);

  useEffect(() => {
    if (result) resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [result]);

  const email = () => {
    const body = JSON.stringify({ type: 'mailto', button_id: 'email' });
    if (!navigator.sendBeacon?.('/api/events', new Blob([body], { type: 'application/json' }))) {
      fetcher('/api/events', { method: 'POST', headers: { 'content-type': 'application/json' }, body, keepalive: true }).catch(() => undefined);
    }
    window.location.href = `mailto:${config.email.address}?subject=${encodeURIComponent(config.email.subject)}`;
  };

  const copyText = async (value: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setMessage(successMessage);
    } catch {
      setMessage('Copy failed. Please select and copy the number manually.');
    }
  };

  const copyEmail = async () => {
    await copyText(config.email.address, 'Email copied.');
    fetcher('/api/events', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ type: 'copy_email', button_id: 'email' }), keepalive: true }).catch(() => undefined);
  };

  const lookup = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    setResult(null);
    const payload = {
      ...(orderNumber.trim() && { order_number: orderNumber.trim() }),
      ...(phone.trim() && { phone: phone.trim() }),
      ...(shippingName.trim() && { shipping_name: shippingName.trim() })
    };
    if (!Object.keys(payload).length) {
      setMessage('Enter at least one field to find your order.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetcher('/api/orders/lookup', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await response.json() as OrderLookupResult | { message?: string };
      if (!response.ok) setMessage('message' in data && data.message ? data.message : config.messages.notFound);
      else setResult(data as OrderLookupResult);
    } catch {
      setMessage(config.messages.unavailable);
    } finally {
      setLoading(false);
    }
  };

  const openTracking = () => {
    if (trackingOpen) trackingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else setTrackingOpen(true);
  };

  const searchAnotherOrder = () => {
    setOrderNumber('');
    setPhone('');
    setShippingName('');
    setResult(null);
    setMessage('');
    setTimeout(() => {
      trackingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      orderInputRef.current?.focus();
    }, 0);
  };

  const enabledButtons = [...config.buttons].filter((button) => button.enabled).sort((a, b) => a.order - b.order);
  return <main className="customer-shell">
    <section className="support-card">
      {config.logo.visible && <div className="brand-logo-crop" data-testid="brand-logo-crop"><img className="brand-logo" src={logoAssets[config.logo.asset]} alt="Tuftlet" /></div>}
      <p className="eyebrow">CUSTOMER CARE</p>
      <h1>{config.title}</h1>
      <p className="intro">{config.description}</p>
      <div className="actions">{enabledButtons.map((button) => {
        if (button.type === 'whatsapp') return config.whatsapp.number
          ? <a key={button.id} className="action primary" href={`/go/${button.id}`}><ActionIcon type="whatsapp" /><span>{button.label}</span></a>
          : <button key={button.id} className="action unavailable-action" aria-label={button.label} disabled><ActionIcon type="whatsapp" /><span className="action-copy"><span>{button.label}</span><small>WhatsApp will be available soon.</small></span></button>;
        if (button.type === 'email') return <button key={button.id} className="action" onClick={email} disabled={!config.email.address}><ActionIcon type="email" /><span>{button.label}</span></button>;
        if (button.type === 'tracking') return <button key={button.id} className="action" aria-expanded={trackingOpen} onClick={openTracking}><ActionIcon type="tracking" /><span>{button.label}</span></button>;
        return <a key={button.id} className="action" href={`/go/${button.id}`}>{button.label}</a>;
      })}</div>

      {config.email.address && <div className="support-email" data-testid="support-email">
        <span>{config.email.address}</span>
        <button type="button" onClick={copyEmail} aria-label="Copy email address">Copy</button>
      </div>}

      {trackingOpen && <form ref={trackingRef} className="tracking-form" onSubmit={lookup}>
        <div className="tracking-title"><SiTiktok aria-hidden="true" /><h2>Track your TikTok order</h2></div>
        <p className="package-context">Find the tracking details for your Tuftlet package shipped separately from your welcome gift.</p>
        <p className="lookup-hint">Enter any one of the fields below to find your order.</p>
        <label>TikTok order number<input ref={orderInputRef} value={orderNumber} onChange={(event) => setOrderNumber(event.target.value)} inputMode="numeric" placeholder="Full order number or last 4/6 digits" autoComplete="off" /></label>
        <label>Phone number used for shipping<input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" placeholder="Example: (415) 555-0122" autoComplete="tel" /></label>
        <label>Full name used for shipping<input value={shippingName} onChange={(event) => setShippingName(event.target.value)} placeholder="Enter the full shipping name" autoComplete="name" /></label>
        <button className="submit" disabled={loading}>{loading ? 'Searching…' : 'Find my order'}</button>
      </form>}

      {message && <p className="notice" role="status">{message}</p>}
      {result && <section ref={resultsRef} className="results" aria-label="Order tracking results">
        <h2 className="results-title">Order tracking details</h2>
        <p className="result-status">Order found</p>
        <p className="sr-only" role="status" aria-live="polite" data-testid="result-announcement">We found {result.orders.length} {result.orders.length === 1 ? 'order' : 'orders'}.</p>
        {result.orders.map((order, index) => <OrderCard key={order.order_number} order={order} index={index} whatsappNumber={config.whatsapp.number} revealOrderNumber={result.query_type === 'order_number'} onCopy={(trackingNumber) => copyText(trackingNumber, 'Tracking number copied.')} />)}
        {result.has_more && <p className="more-orders"><span>Showing your two most recent orders. Need help with an earlier order?</span>{' '}{config.whatsapp.number ? <a href={whatsappUrl(config.whatsapp.number, 'Hi Tuftlet, I need help finding an earlier TikTok order.')!} target="_blank" rel="noreferrer">Chat with us on WhatsApp</a> : 'Chat with us on WhatsApp.'}</p>}
        <button type="button" className="search-another" onClick={searchAnotherOrder}>Search another order</button>
      </section>}
    </section>
  </main>;
}
